// Lightweight Web Audio helpers for the sorting visualizer.
//
// Tones are synthesized on the fly (no audio files) and pitched from a bar's
// value using a major-pentatonic scale so the result stays musical rather
// than sounding like a random sweep of noise.

let audioContext = null

// Major pentatonic intervals (in semitones) repeated across octaves.
const PENTATONIC = [0, 2, 4, 7, 9]

export function ensureAudioContext() {
  if (typeof window === 'undefined') return null

  const AudioCtx = window.AudioContext || window.webkitAudioContext
  if (!AudioCtx) return null

  if (!audioContext) {
    audioContext = new AudioCtx()
  }

  if (audioContext.state === 'suspended') {
    audioContext.resume()
  }

  return audioContext
}

// Map a bar value to a frequency. Higher values produce higher pitches while
// always landing on a pentatonic note, so any combination sounds consonant.
export function valueToFrequency(value, min = 5, max = 100) {
  const clamped = Math.min(1, Math.max(0, (value - min) / (max - min)))
  const steps = Math.round(clamped * 10) // 11 notes spanning two octaves
  const octave = Math.floor(steps / 5)
  const degree = steps % 5
  const semitones = octave * 12 + PENTATONIC[degree]
  return 220 * Math.pow(2, semitones / 12)
}

export function playTone({ frequency, duration = 0.08, type = 'sine', volume = 0.05, delay = 0 }) {
  const ctx = ensureAudioContext()
  if (!ctx || ctx.state !== 'running') return

  const start = ctx.currentTime + delay
  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()

  oscillator.type = type
  oscillator.frequency.setValueAtTime(frequency, start)

  gain.gain.setValueAtTime(volume, start)
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)

  oscillator.connect(gain)
  gain.connect(ctx.destination)

  oscillator.start(start)
  oscillator.stop(start + duration)
}
